import React from 'react';
import type { CollapseProps } from 'antd';
import { Dayjs } from 'dayjs';
import { Collapse, ConfigProvider } from 'antd';
import { Form, useFormikContext } from 'formik';
import {
  AphDatePicker,
  AphSelect,
  useFetchServiceV3
} from '@aph/common-react';
import { defaultPageSize } from 'global.constants';
import { updateUrlSearchParameters } from 'global.helpers';
import { setupFilteredTagsList } from 'helpers/tag.helpers';
import { IPublicationSearchFormData } from 'types/form.types';
import {
  handleRemoveSelectedSearchFilterTag,
  setupDateForRequest
} from 'helpers/form.helpers';
import {
  IPublicationSearchRequest,
  IUseSearchService,
  IPublicationSearchResult,
  IGetLookupResponse
} from 'services';

import {
  NoResultsFound,
  SearchBar,
  SearchFilteredTags,
  SearchResultItem,
  SearchResultsWrapper
} from 'components';

/**
 * IPublicationSearchInner
 *
 * @description Component properties for the publication search component
 */
interface IPublicationSearchInner {
  searchService: IUseSearchService;
  currentSearchRequest: IPublicationSearchRequest;
  searchResults: {
    loading: boolean;
    results: Array<IPublicationSearchResult>;
    currentPage: number;
    pageSize: number;
    rowCount: number;
  };
  filterSearchResults: (request: IPublicationSearchRequest) => Promise<void>;
  paginationHandler: (selectedPage: number) => Promise<void>;
}

/**
 * PublicationSearchInner
 *
 * @description
 */
const PublicationSearchInner: React.FC<IPublicationSearchInner> = ({
  searchService,
  searchResults,
  currentSearchRequest,
  filterSearchResults,
  paginationHandler
}) => {
  const { items: categories } = useFetchServiceV3<undefined, IGetLookupResponse>(searchService.getSearchCategories, { loadOnInitiate: true });
  const { items: publicationTypes } = useFetchServiceV3<undefined, IGetLookupResponse>(searchService.getSearchPublicationTypes, { loadOnInitiate: true });
  const { values: formValues, setFieldValue } = useFormikContext<IPublicationSearchFormData>();

  /**
   * HandleFormFieldOnChange
   *
   * @description On change handler for filters
   * @param {string}                 key   Form field name
   * @param {string | Array<string>} value Field value
   * @returns {void}
   */
  const handleFormFieldOnChange = (key: string, value: string | Array<string>): void => {
    updateUrlSearchParameters(key, value);

    const updatedSearchRequest: IPublicationSearchRequest = {
      ...currentSearchRequest,
      currentPage: 1,
      [key]: value
    };

    filterSearchResults(updatedSearchRequest);
  };

  /**
   * HandleDateFromFieldOnChange
   *
   * @description On change handler for the date field
   * @param {Dayjs | null} value Selected date values
   * @returns {void}
   */
  const handleDateFromFieldOnChange = (value: Dayjs | null | null): void => {
    const updatedUrlParameter = value ? value.format('YYYY-MM-DD') : null;
    updateUrlSearchParameters('dateFrom', updatedUrlParameter);

    const dataRequestSetup = setupDateForRequest(value);
    const updatedSearchRequest: IPublicationSearchRequest = {
      ...currentSearchRequest,
      currentPage: 1,
      dateFrom: dataRequestSetup,
    };

    filterSearchResults(updatedSearchRequest);
  };

  /**
   * HandleDateToFieldOnChange
   *
   * @description On change handler for the date field
   * @param {Dayjs | null} value Selected date values
   * @returns {void}
   */
  const handleDateToFieldOnChange = (value: Dayjs | null | null): void => {
    const updatedUrlParameter = value ? value.format('YYYY-MM-DD') : null;
    updateUrlSearchParameters('dateTo', updatedUrlParameter);

    const dataRequestSetup = setupDateForRequest(value);
    const updatedSearchRequest: IPublicationSearchRequest = {
      ...currentSearchRequest,
      currentPage: 1,
      dateTo: dataRequestSetup
    };

    filterSearchResults(updatedSearchRequest);
  };

  // This defines the content for the collapse
  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'Filters',
      children:
        <div className='library-publication-search__filters-inner'>
          <div className='library-publication-search__filter-column'>
            <h3>Topics</h3>
            <AphSelect
              name='topics'
              mode='multiple'
              placeholder='Start typing to select topics...'
              options={categories ?? []}
              allowClear
              onChange={(value: string | Array<string>) => handleFormFieldOnChange('topics', value)}
            />
          </div>
          <div className='library-publication-search__filter-column'>
            <h3>Type</h3>
            <AphSelect
              name='publicationTypes'
              mode='multiple'
              placeholder='Select publication types'
              allowClear
              onChange={(value: string | Array<string>) => handleFormFieldOnChange('publicationTypes', value)}
              options={publicationTypes ?? []}
            />
          </div>
          <div className='library-publication-search__filter-column'>
            <h3>When</h3>
            <div className='library-publication-search__filter-date'>
              <AphDatePicker
                name='dateFrom'
                placeholder='Start date'
                onChange={(value: Dayjs | null) => handleDateFromFieldOnChange(value)}
              />
              <AphDatePicker
                name='dateTo'
                placeholder='End date'
                onChange={(value: Dayjs | null) => handleDateToFieldOnChange(value)}
              />
            </div>
          </div>
        </div>
    }
  ];

  return (
    <div className='library-publication-search'>
      <Form>
        <div className='library-publication-search__filters'>
          <SearchBar theme='publications' name='searchTerms' showClear />
          <ConfigProvider
            theme={{
              token: {
                borderRadiusLG: 0,
                lineWidth: 0
              }
            }}
          >
            <Collapse
              className='library-publication-search__filters--additional'
              defaultActiveKey={['1']}
              items={items}
            />
          </ConfigProvider>
        </div>
        <SearchResultsWrapper
          loading={searchResults.loading && (!searchResults.results || searchResults.results.length <= 0)}
          paginationConfig={{
            currentPage: searchResults.currentPage ?? 1,
            totalResults: searchResults.rowCount ?? 0,
            pageSize: searchResults.pageSize ?? defaultPageSize,
            onPaginate: paginationHandler
          }}
        >
          <SearchFilteredTags
            tags={[
              ...setupFilteredTagsList('topics', formValues.topics, categories),
              ...setupFilteredTagsList('publicationTypes', formValues.publicationTypes, publicationTypes)
            ]}
            onRemove={(id: string, fieldName: string) =>
              handleRemoveSelectedSearchFilterTag(fieldName, id, formValues[fieldName as 'topics' | 'publicationTypes'], setFieldValue, handleFormFieldOnChange)}
          />
          {
            searchResults.results &&
              searchResults.results.length > 0
              ?
              <>
                {
                  searchResults.results.map(searchResult =>
                    <SearchResultItem
                      key={searchResult.id}
                      result={searchResult}
                    />
                  )
                }
              </>
              : <NoResultsFound />
          }
        </SearchResultsWrapper>
      </Form>
    </div>
  );
};

export default PublicationSearchInner;
